<template>
    <div class="pizarra">
        <div class="chart-wrapper">
            <!-- Gráfica W1 (Mañana) -->
            <div class="chart-container col-md-9 col-sm-12">
                <h2>Estado Emocional de Empleados - Jornada Mañana</h2>
                <div v-if="hasDataW1">
                    <line-chart 
                        :chart-data="chartDataW1" 
                        :options="baseChartOptions"
                    />
                </div>
                <div v-else class="no-data-message">
                    <p>No hay datos disponibles para la jornada mañana</p>
                </div>
            </div>

            <!-- Gráfica W2 (Tarde) -->
            <div class="chart-container col-md-9 col-sm-12">
                <h2>Estado Emocional de Empleados - Jornada Tarde</h2>
                <div v-if="hasDataW2">
                    <line-chart 
                        :chart-data="chartDataW2" 
                        :options="baseChartOptions"
                    />
                </div>
                <div v-else class="no-data-message">
                    <p>No hay datos disponibles para la jornada tarde</p>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
import { Line } from 'vue-chartjs';

// Componente de gráfica de líneas personalizado
const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartData, this.options)
      }
    }
  }
}

import {
    HAPPINESS_M,
    ACTION_GET_RESULTS_MONTHLY_CHART,
    GET_RESULTS_MONTHLY_CHART,
} from "@/views/chart/store/types/types.js";

import { mapActions, mapGetters } from "vuex";

export default {
    name: "monthlyLineChartComponent",
    components: {
        LineChart
    },
    data() {
        return {
            resultsMonthly: null,
            windowsData: {
                W1: {
                    labels: [],
                    datasets: []
                },
                W2: {
                    labels: [],
                    datasets: []
                }
            },
            baseChartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        align: 'center',
                        labels: {
                            boxWidth: 15,
                            padding: 20,
                            color: '#333',
                            font: {
                                size: 12,
                                weight: '500'
                            },
                            usePointStyle: true
                        }
                    }
                },
                scales: {
                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Cantidad de Empleados',
                            color: '#2D0049',
                            font: {
                                size: 14,
                                weight: 'bold',
                                family: 'Arial, sans-serif'
                            },
                            padding: {
                                top: 10,
                                bottom: 10
                            }
                        },
                        beginAtZero: true,
                        grid: {
                            display: true,
                            color: '#e0e0e0',
                            borderDash: [2, 2]
                        },
                        ticks: {
                            color: '#666',
                            font: {
                                size: 11
                            }
                        }
                    },
                    x: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Días de la Semana',
                            color: '#2D0049',
                            font: {
                                size: 14,
                                weight: 'bold',
                                family: 'Arial, sans-serif'
                            },
                            padding: {
                                top: 10,
                                bottom: 5
                            }
                        },
                        grid: {
                            display: false
                        },
                        ticks: {
                            color: '#666',
                            font: {
                                size: 11
                            }
                        }
                    }
                },
                elements: {
                    point: {
                        radius: 4,
                        hoverRadius: 6,
                        borderWidth: 2
                    },
                    line: {
                        tension: 0.4,
                        borderWidth: 3
                    }
                },
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                animation: {
                    duration: 1000,
                    easing: 'easeOutQuart'
                }
            },
        }
    },
    
    async mounted() {
        await this.loadChartData()
    },
    
    methods: {
        async loadChartData() {
            try {
                // Cargar datos dinámicos del servicio
                this.resultsMonthly = await this.actionGetresultsMonthlyChart()
                // Procesar datos del servicio
                this.processChartData()
            } catch (err) {
                console.error("Error cargando datos:", err)
            }
        },

        // Método para procesar datos dinámicos del servicio
        processChartData() {
            if (this.resultsMonthly && 
                this.resultsMonthly.data && 
                this.resultsMonthly.data.payload && 
                this.resultsMonthly.data.payload.windows) {
                const windows = this.resultsMonthly.data.payload.windows;
                // Procesar W1 (Mañana)
                if (windows.W1) {
                    this.windowsData.W1 = {
                        labels: windows.W1.labels || [],
                        datasets: windows.W1.datasets || []
                    };
                }
                
                // Procesar W2 (Tarde)
                if (windows.W2) {
                    this.windowsData.W2 = {
                        labels: windows.W2.labels || [],
                        datasets: windows.W2.datasets || []
                    };
                }
            }
        },

        // Obtener colores para cada dataset
        getDatasetColors(label) {
            const colorMap = {
                'Alegría': {
                    backgroundColor: 'rgba(28, 185, 16, 0.1)',
                    borderColor: '#1cb910',
                    pointBackgroundColor: '#1cb910'
                },
                'Tristeza': {
                    backgroundColor: 'rgba(185, 16, 16, 0.1)',
                    borderColor: '#b91010',
                    pointBackgroundColor: '#b91010'
                },
                'Neutral': {
                    backgroundColor: 'rgba(185, 181, 16, 0.1)',
                    borderColor: '#b9b510',
                    pointBackgroundColor: '#b9b510'
                }
            };
            
            return colorMap[label] || {
                backgroundColor: 'rgba(128, 128, 128, 0.1)',
                borderColor: '#808080',
                pointBackgroundColor: '#808080'
            };
        },

        // Método para verificar si hay datos válidos en un dataset
        hasValidData(windowData) {
            if (!windowData.labels || !windowData.datasets) {
                return false;
            }
            
            if (windowData.labels.length === 0 || windowData.datasets.length === 0) {
                return false;
            }
            
            // Verificar si al menos un dataset tiene datos válidos
            return windowData.datasets.some(dataset => 
                dataset.data && 
                dataset.data.length > 0 && 
                dataset.data.some(value => value !== null && value !== undefined && value !== 0)
            );
        },

        ...mapActions({
            actionGetresultsMonthlyChart: `${HAPPINESS_M}${ACTION_GET_RESULTS_MONTHLY_CHART}`,
        }),
    },
    
    computed: {
        // Verificar si W1 (Mañana) tiene datos
        hasDataW1() {
            return this.hasValidData(this.windowsData.W1);
        },

        // Verificar si W2 (Tarde) tiene datos
        hasDataW2() {
            return this.hasValidData(this.windowsData.W2);
        },

        // Datos de W1 (Mañana)
        chartDataW1() {
            const windowData = this.windowsData.W1;
            if (!windowData.labels.length || !windowData.datasets.length) {
                return { labels: [], datasets: [] };
            }
            
            const processedDatasets = windowData.datasets.map(dataset => {
                const colors = this.getDatasetColors(dataset.label);
                
                return {
                    label: dataset.label,
                    data: dataset.data,
                    backgroundColor: colors.backgroundColor,
                    borderColor: colors.borderColor,
                    pointBackgroundColor: colors.pointBackgroundColor,
                    pointBorderColor: '#ffffff',
                    fill: true
                };
            });
            
            return {
                labels: windowData.labels,
                datasets: processedDatasets
            };
        },

        // Datos de W2 (Tarde)
        chartDataW2() {
            const windowData = this.windowsData.W2;
            if (!windowData.labels.length || !windowData.datasets.length) {
                return { labels: [], datasets: [] };
            }
            
            const processedDatasets = windowData.datasets.map(dataset => {
                const colors = this.getDatasetColors(dataset.label);
                
                return {
                    label: dataset.label,
                    data: dataset.data,
                    backgroundColor: colors.backgroundColor,
                    borderColor: colors.borderColor,
                    pointBackgroundColor: colors.pointBackgroundColor,
                    pointBorderColor: '#ffffff',
                    fill: true
                };
            });
            
            return {
                labels: windowData.labels,
                datasets: processedDatasets
            };
        },

        ...mapGetters({
            resultsMonthlyChart: `${HAPPINESS_M}${GET_RESULTS_MONTHLY_CHART}`,
        }),
    },
};
</script>
  
<style lang="css" scoped>
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-family: 'Amsi Pro', sans-serif;
        color: #2D0049;
        font-weight: normal;
    }

    .chart-container {
        margin: auto;
        font-family: 'Arial', sans-serif;
        padding: 20px;
    }

    .chart-wrapper {
        position: relative;
        padding: 20px;
        text-align: center;
    }

    .no-data-message {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
    }

    .no-data-message p {
        font-size: 16px;
        color: #6c757d;
        margin: 0;
    }

    /* Responsive */
    @media (max-width: 600px) {        
        .chart-container {
            padding: 15px;
        }

        .chart-wrapper {
            padding: 10px;
        }

        .no-data-message p {
            font-size: 14px;
        }
    }
</style>